(function () {
  const selector = 'video[data-viewport-autoplay]';
  const escapeAttribute = value => String(value ?? '').replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]));

  function resolveImageSource(source, baseUrl) {
    if (!source) return '';
    let url;
    try { url = new URL(source, baseUrl); } catch { return null; }
    if (!['http:', 'https:'].includes(url.protocol)) return null;
    const resizable = url.hostname === 'cdn.shopify.com' || url.pathname.startsWith('/cdn/shop/');
    return { resizable, atWidth(width) {
      const candidate = new URL(url);
      if (resizable) candidate.searchParams.set('width', width);
      return candidate.href;
    } };
  }

  function createImageRenderer({ resolveSource = resolveImageSource, baseUrl = 'https://cdn.shopify.com', widths = [180, 240, 360, 480, 720, 1000], sizes = 'auto, (min-width: 990px) 25vw, 50vw' } = {}) {
    return (source, alt = '') => {
      const image = resolveSource(source, baseUrl);
      if (!image) return '';
      const srcset = image.resizable ? ` srcset="${widths.map(width => `${escapeAttribute(image.atWidth(width))} ${width}w`).join(', ')}"` : '';
      return `<img src="${escapeAttribute(image.atWidth(Math.max(...widths)))}"${srcset} sizes="${escapeAttribute(sizes)}" alt="${escapeAttribute(alt)}" loading="lazy" decoding="async">`;
    };
  }

  function loadInteractionImages(root) {
    root.querySelectorAll('[data-interaction-srcset], [data-interaction-src]').forEach(image => {
      ['srcset', 'src'].forEach(attribute => {
        const deferredAttribute = `data-interaction-${attribute}`;
        const source = image.getAttribute(deferredAttribute);
        if (!source) return;
        image.setAttribute(attribute, source);
        image.removeAttribute(deferredAttribute);
      });
    });
  }

  function containingSlides(video) {
    const slides = [];
    for (let slide = video.closest?.('.swiper-slide'); slide; slide = slide.parentElement?.closest('.swiper-slide')) slides.push(slide);
    return slides;
  }

  const activeSlidePolicy = video => containingSlides(video).every(slide => slide.classList.contains('swiper-slide-active'));
  const matchingVideos = root => [...(root.matches?.(selector) ? [root] : []), ...Array.from(root.querySelectorAll?.(selector) || [])];

  class VideoSlideObserver {
    constructor({ onChange, createMutationObserver = callback => new MutationObserver(callback), getTargets = containingSlides }) {
      this.targets = new Map();
      this.getTargets = getTargets;
      this.observer = createMutationObserver(() => onChange());
    }

    initialize(root) {
      let changed = false;
      matchingVideos(root).forEach(video => {
        const targets = this.getTargets(video);
        const previous = this.targets.get(video);
        if (previous && previous.length === targets.length && previous.every((target, index) => target === targets[index])) return;
        this.targets.set(video, targets);
        changed = true;
      });
      if (changed) this.observeTargets();
    }

    observeTargets() {
      this.observer.disconnect();
      const targets = new Set([...this.targets.values()].flat());
      targets.forEach(target => this.observer.observe(target, { attributes: true, attributeFilter: ['class'] }));
    }

    release(root) {
      let changed = false;
      this.targets.forEach((_, video) => {
        if (root !== video && !root.contains?.(video)) return;
        this.targets.delete(video);
        changed = true;
      });
      if (changed) this.observeTargets();
    }

    disconnect() { this.observer.disconnect(); this.targets.clear(); }
  }

  class ViewportVideoPlayback {
    constructor({ createObserver = callback => new IntersectionObserver(callback), canPlay = activeSlidePolicy } = {}) {
      this.visible = new Set();
      this.registered = new Set();
      this.requested = new WeakMap();
      this.canPlay = canPlay;
      this.observer = createObserver(entries => entries.forEach(entry => {
        if (!this.registered.has(entry.target)) return;
        if (entry.isIntersecting) this.visible.add(entry.target);
        else this.visible.delete(entry.target);
        this.sync(entry.target);
      }));
    }

    sync(video) {
      if (!this.registered.has(video)) return;
      if (!this.visible.has(video) || !this.canPlay(video)) {
        this.requested.delete(video);
        video.pause();
        return;
      }
      if (this.requested.has(video)) return;
      const request = {};
      this.requested.set(video, request);
      video.play()?.catch(() => {
        if (this.requested.get(video) === request) this.requested.delete(video);
      });
    }

    initialize(root) {
      matchingVideos(root).forEach(video => {
        if (this.registered.has(video)) return;
        this.registered.add(video);
        this.observer.observe(video);
      });
    }

    refresh() { this.visible.forEach(video => this.sync(video)); }

    release(root) {
      this.registered.forEach(video => {
        if (root !== video && !root.contains?.(video)) return;
        this.observer.unobserve(video);
        this.visible.delete(video);
        this.registered.delete(video);
        this.requested.delete(video);
        video.pause();
      });
    }

    disconnect() {
      this.observer.disconnect();
      this.registered.forEach(video => {
        this.requested.delete(video);
        video.pause();
      });
      this.registered.clear();
      this.visible.clear();
    }
  }

  function mountViewportVideos({ root, playback, createMutationObserver = callback => new MutationObserver(callback), slideObserver = new VideoSlideObserver({ onChange: () => playback.refresh(), createMutationObserver }) }) {
    const initializeNode = node => { playback.initialize(node); slideObserver.initialize(node); };
    const releaseNode = node => { playback.release(node); slideObserver.release(node); };
    const initialize = () => initializeNode(root);
    const sectionLoaded = event => initializeNode(event.target);
    const sectionUnloaded = event => releaseNode(event.target);
    root.addEventListener('shopify:section:load', sectionLoaded);
    root.addEventListener('shopify:section:unload', sectionUnloaded);
    if (root.readyState === 'loading') root.addEventListener('DOMContentLoaded', initialize, { once: true });
    else initialize();
    const observer = createMutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.removedNodes.forEach(node => { if (!node.isConnected) releaseNode(node); });
        mutation.addedNodes.forEach(node => initializeNode(node));
      });
    });
    observer.observe(root.documentElement, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      root.removeEventListener('DOMContentLoaded', initialize);
      root.removeEventListener('shopify:section:load', sectionLoaded);
      root.removeEventListener('shopify:section:unload', sectionUnloaded);
      playback.disconnect();
      slideObserver.disconnect();
    };
  }

  const imageMarkup = createImageRenderer({ baseUrl: typeof document === 'undefined' ? 'https://cdn.shopify.com' : document.baseURI });
  const api = { imageMarkup, createImageRenderer, resolveImageSource, loadInteractionImages, activeSlidePolicy, VideoSlideObserver, ViewportVideoPlayback, mountViewportVideos };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  window.HOMResponsiveMedia = api;
  if (typeof IntersectionObserver !== 'undefined') mountViewportVideos({ root: document, playback: new ViewportVideoPlayback() });
})();
